import { useState, useMemo, useEffect } from 'react';

const getVal = (item, key) => {
  if (key.includes('|')) {
    for (const k of key.split('|')) {
      const v = k
        .trim()
        .split('.')
        .reduce((o, kk) => o?.[kk], item);
      if (v != null && v !== '') return v;
    }
    return '';
  }
  return key.split('.').reduce((o, k) => o?.[k], item) ?? '';
};

/**
 * Client-side sort + pagination for pre-filtered arrays.
 * @param {Array}  items        Already-filtered array from the parent hook.
 * @param {Object} defaultSort  { col, dir } — initial sort state.
 * @param {number} pageSize     Rows per page (default 15).
 * @param {Array}  resetDeps    When these values change, reset to page 1.
 *                              Pass the filters object(s) that produced `items`.
 */
export function useSortPaginate(items, defaultSort = { col: null, dir: 'asc' }, pageSize = 15, resetDeps = []) {
  const [page, setPageState] = useState(1);
  const [sortCol, setSortCol] = useState(defaultSort.col);
  const [sortDir, setSortDir] = useState(defaultSort.dir);

  // Reset to page 1 only when the caller's filter state changes, not on every render.

  useEffect(() => {
    setPageState(1);
  }, resetDeps);

  const sorted = useMemo(() => {
    if (!sortCol) return items;
    return [...items].sort((a, b) => {
      const av = getVal(a, sortCol);
      const bv = getVal(b, sortCol);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [items, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const setPage = (p) => setPageState(Math.max(1, Math.min(p, totalPages)));

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPageState(1);
  };

  return {
    paginated,
    page: safePage,
    totalPages,
    totalItems: sorted.length,
    pageSize,
    sortCol,
    sortDir,
    setPage,
    toggleSort,
  };
}
